import clsx from 'clsx'
import type {
  MouseEventHandler,
  ComponentProps,
} from 'react'
import { useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'

import type { EllipsisMenuProps } from './common'

import ResponsiveDropdownList from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownList'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import useToggle from 'hooks/useToggle'
import AssistChip from 'LuxKit/Chips/AssistChip'

interface Props extends EllipsisMenuProps {
  className?: string
  size?: ComponentProps<typeof AssistChip>['size']
  variant?: ComponentProps<typeof AssistChip>['variant']
}

export default function MenuButton({
  className,
  title,
  subtitle,
  bodyContent,
  footerContent,
  size = 'small',
  variant = 'outline',
}: Props) {
  const intl = useIntl()

  const {
    value: dropdownOpen,
    toggle: toggleDropdown,
    off: closeDropdown,
  } = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)

  const toggleMenu: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      toggleDropdown()
    },
    [toggleDropdown],
  )

  const onContentClick: MouseEventHandler<HTMLDivElement> = useCallback(e => {
    // If user clicked a button or a link within the dropdown, close the dropdown
    if (e.target instanceof HTMLElement) {
      const linkOrButton = e.target.closest('button, a')
      if (e.currentTarget.contains(linkOrButton)) {
        closeDropdown()
      }
    }
  }, [closeDropdown])

  return (
    <>
      <AssistChip
        variant={variant}
        size={size}
        data-testid="action-menu-button"
        aria-label={intl.formatMessage({
          id: 'sgHzdflfCcLBnwvXyU3e',
          defaultMessage: 'action menu',
          description: 'Aria label for the action menu button on trip planner items',
        })}
        onClick={toggleMenu}
        className={clsx(className, 'no-print')}
        ref={triggerRef}
        startIcon={<LineEllipsisVIcon />}
      />
      <ResponsiveDropdownList
        size="S"
        triggerRef={triggerRef}
        anchorRef={triggerRef}
        open={dropdownOpen}
        onClose={closeDropdown}
        placement="bottom-end"
        portaled
        mobileTitle={title}
        mobileSubtitle={subtitle}
      >
        <div onClick={onContentClick}>
          {bodyContent}
          {footerContent}
        </div>
      </ResponsiveDropdownList>
    </>
  )
}
